import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import EnLayout from '@/components/feature/EnLayout';
import useSEO from '@/hooks/useSEO';
import SEOJsonLd from '@/components/base/SEOJsonLd';

const FORM_URL = 'https://readdy.ai/api/form/d7kdndl0lfokr4m6je10';

const productTypes = [
  'Hard Capsules',
  'Soft Gel Capsules',
  'Tablets',
  'Chewable Tablets',
  'Powder Sachets / Granules',
  'Functional Beverages',
  'Oral Liquid',
  'Gummies',
  'Jelly Sticks',
  'Tea Bags',
  'Other Dosage Forms',
];

const quantities = [
  '1,000 – 3,000 units',
  '3,000 – 10,000 units',
  '10,000 – 50,000 units',
  '50,000+ units',
  'Not yet decided',
];

const formulaOptions = [
  'I have a specific formula idea',
  'I have a concept, need assistance',
  'No idea yet, please suggest',
];

const processSteps = [
  {
    step: '01',
    title: 'Submit Your Inquiry',
    desc: 'Fill in your product direction, estimated quantity, dosage form requirements and contact details.',
    icon: 'ri-file-edit-line',
  },
  {
    step: '02',
    title: 'Initial Response from Our Team',
    desc: 'Kang Yong Biotech will contact you as soon as possible to confirm your core needs and collaboration direction.',
    icon: 'ri-customer-service-2-line',
  },
  {
    step: '03',
    title: 'Needs Assessment & Feasibility Review',
    desc: 'Based on your brand positioning, sales channels, budget and product goals, we help evaluate suitable formulas, dosage forms and packaging options.',
    icon: 'ri-search-eye-line',
  },
  {
    step: '04',
    title: 'Preliminary Quote & Collaboration Proposal',
    desc: 'After confirming requirements, we provide an initial collaboration proposal, timeline estimate and pricing assessment to help you move into the next development phase.',
    icon: 'ri-price-tag-3-line',
  },
];

export default function EnContactPage() {
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const siteUrl = (window as any).__VITE_SITE_URL__ || 'https://kangyong.com';
  useSEO({
    title: 'Contact Kang Yong Biotech | OEM ODM Health Supplement Inquiry',
    description: 'Contact Kang Yong Biotech for OEM/ODM health supplement manufacturing inquiries. Reach us by form, phone (+886-4-2202-9858), email, or LINE @126uwznp. Taichung, Taiwan.',
    keywords: 'contact Kang Yong, OEM ODM inquiry, health supplement contact, supplement manufacturer Taiwan, Kang Yong phone, Kang Yong LINE',
    lang: 'en',
    canonicalPath: '/en/contact',
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="requirement"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setSubmitState('loading');
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitState('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitState('error');
      }
    } catch {
      setSubmitState('error');
    }
  };

  return (
    <EnLayout>
      <SEOJsonLd type="WebPage" pageTitle="Contact Kang Yong Biotech | OEM ODM Health Supplement Inquiry" pageDescription="Contact Kang Yong Biotech for OEM/ODM health supplement manufacturing inquiries." pageUrl={`${siteUrl}/en/contact`} />
      {/* Hero - matching ContactInfo hero style */}
      <section
        className="relative pt-24 pb-16 bg-cover bg-top"
        style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=natural%20green%20office%20entrance%20building%20exterior%20with%20lush%20plants%20clean%20minimalist%20architecture%20soft%20morning%20sunlight%20warm%20inviting%20biotech%20company%20facade%20nature%20inspired%20design%20serene%20environment&width=1600&height=560&seq=contact-info-hero&orientation=landscape')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />
        <div className="relative max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-2 mb-4 text-white/60 text-[0.8125rem]">
            <Link to="/en" className="hover:text-white transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/90">Contact Us</span>
          </div>
          <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-[0.8125rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-white/30">
            Contact Us
          </span>
          <h1 className="text-white font-bold text-[2.25rem] md:text-[3rem] leading-tight mb-4 max-w-2xl">
            Contact Information
          </h1>
          <p className="text-white/85 text-[1.0625rem] leading-[1.8] max-w-xl md:whitespace-nowrap">
            Reach us by phone, email, or LINE — our team is happy to assist with your OEM/ODM inquiry.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="https://lin.ee/X7Hcpf4"
              rel="nofollow"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.9375rem] px-6 py-3 rounded-full transition-all duration-300 cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-line-fill text-base" /></div>
              Join LINE Official Account
            </a>
            <a
              href="tel:+886422029858"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 border border-white/70 text-white hover:bg-white/10 font-semibold text-[0.9375rem] px-6 py-3 rounded-full transition-all duration-300 cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-line text-base" /></div>
              Call Us
            </a>
            <a
              href="mailto:natural1070606@gmail.com"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 border border-white/70 text-white hover:bg-white/10 font-semibold text-[0.9375rem] px-6 py-3 rounded-full transition-all duration-300 cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-line text-base" /></div>
              Email Us
            </a>
          </div>
          <div className="mt-6 h-0.5 w-14 bg-[#81C784] rounded-full" />
        </div>
      </section>

      {/* Notice bar */}
      <div className="bg-[#e8f5e9] border-y border-[#c8e6c9]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center gap-4">
          {[
            { icon: 'ri-time-line', text: 'Prompt response to your inquiry' },
            { icon: 'ri-shield-check-line', text: 'Data privacy protected' },
            { icon: 'ri-customer-service-2-line', text: 'Dedicated specialist service' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-1.5 text-[#388E3C] text-[0.8125rem] font-medium">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-sm`} />
              </div>
              {item.text}
            </div>
          ))}
        </div>
      </div>

      {/* Process Steps Section */}
      <section className="py-14 bg-[#f7faf7] border-b border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="mb-8">
            <h2 className="text-[#1a2e1a] font-bold text-[1.375rem] md:text-[1.625rem] mb-2">
              What Happens After You Submit?
            </h2>
            <p className="text-gray-500 text-[0.9rem] leading-[1.75]">
              From inquiry to development — here's our clear collaboration flow.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {processSteps.map((s, idx) => (
              <div key={s.step} className="relative bg-white rounded-xl p-5 border border-gray-100">
                {idx < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-[2.25rem] left-full w-5 h-0.5 bg-[#c8e6c9] z-10" />
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#e8f5e9] rounded-full flex-shrink-0 border border-[#c8e6c9]">
                    <i className={`${s.icon} text-[#4CAF50] text-base`} />
                  </div>
                  <span className="text-[#4CAF50] font-bold text-[0.75rem] tracking-widest">STEP {s.step}</span>
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.75]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Form Section */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 xl:gap-16 items-start">

            {/* Left: sidebar info */}
            <div className="w-full lg:w-[300px] xl:w-[340px] flex-shrink-0 space-y-5">
              <div>
                <h2 className="text-[#1a2e1a] font-bold text-[1.25rem] mb-1.5">Before You Fill In</h2>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75]">
                  The information below can help you complete the form faster and improve communication efficiency.
                </p>
              </div>

              {[
                { icon: 'ri-flask-line', title: 'Product Development Consultation', desc: 'Whether developing a new formula, selecting a dosage form, or modifying an existing formula — we\'re here to discuss your needs.' },
                { icon: 'ri-leaf-line', title: 'Natural Ingredient Sourcing', desc: 'We are committed to natural ingredients and can help you select raw material combinations that align with your brand positioning.' },
                { icon: 'ri-file-shield-2-line', title: 'Regulatory & Label Compliance', desc: 'We assist with product regulatory risk assessment and label direction to ensure compliant market launch.' },
                { icon: 'ri-box-3-line', title: 'Packaging Design Services', desc: 'From packaging material selection to visual design, we provide end-to-end packaging planning support.' },
              ].map((tip) => (
                <div key={tip.title} className="flex items-start gap-3 bg-[#f7faf7] rounded-xl p-4 border border-gray-100">
                  <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                    <i className={`${tip.icon} text-[#4CAF50] text-base`} />
                  </div>
                  <div>
                    <p className="text-[#1a2e1a] font-semibold text-[0.875rem] mb-0.5">{tip.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.7]">{tip.desc}</p>
                  </div>
                </div>
              ))}

              {/* Direct contact */}
              <div className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-3">Contact Us Directly</p>
                <div className="space-y-2.5">
                  <a href="tel:+886422029858" className="flex items-center gap-2.5 text-gray-600 hover:text-[#4CAF50] transition-colors cursor-pointer text-[0.875rem]">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200">
                      <i className="ri-phone-line text-[#4CAF50] text-sm" />
                    </div>
                    +886-4-2202-9858
                  </a>
                  <a href="mailto:natural1070606@gmail.com" className="flex items-center gap-2.5 text-gray-600 hover:text-[#4CAF50] transition-colors cursor-pointer text-[0.875rem] break-all">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-shrink-0">
                      <i className="ri-mail-line text-[#4CAF50] text-sm" />
                    </div>
                    natural1070606@gmail.com
                  </a>
                </div>
              </div>

              {/* LINE sidebar */}
              <div className="bg-[#e8f5e9] border border-[#c8e6c9] rounded-xl p-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-line-fill text-[#4CAF50] text-xl" />
                  </div>
                  <p className="text-[#1a2e1a] font-bold text-[0.9375rem]">Chat on LINE</p>
                </div>
                <p className="text-gray-600 text-[0.8125rem] leading-[1.7] mb-3">
                  A faster way to communicate — join our LINE Official Account to chat with us directly.
                </p>
                <a
                  href="https://lin.ee/X7Hcpf4"
                  rel="nofollow"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-4 py-2.5 rounded-full transition-all duration-300 cursor-pointer w-full"
                >
                  <i className="ri-line-fill" />
                  Join LINE Now
                </a>
              </div>
            </div>

            {/* Right: Form */}
            <div className="w-full lg:flex-1">
              <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] mb-2">Inquiry Form</h2>
              <p className="text-gray-500 text-[0.9375rem] mb-7 leading-[1.75]">
                Please fill in the fields below and we'll assign a specialist to respond based on your needs.
              </p>

              {submitState === 'success' ? (
                <div className="flex flex-col items-center text-center bg-[#f7faf7] rounded-2xl border border-green-100 overflow-hidden">
                  <div className="w-full px-8 pt-12 pb-8">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-5 border border-green-200 mx-auto">
                      <i className="ri-checkbox-circle-fill text-[#4CAF50] text-3xl" />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.375rem] mb-3">Inquiry Submitted — Thank You!</h3>
                    <p className="text-gray-500 text-[0.9375rem] leading-[1.75] max-w-sm mx-auto">
                      Kang Yong Biotech will contact you shortly to discuss your product direction, dosage form, estimated quantity and collaboration approach.
                    </p>
                  </div>

                  <div className="w-full bg-[#e8f5e9] border-t border-[#c8e6c9] px-6 sm:px-8 py-6">
                    <p className="text-[#1a2e1a] font-semibold text-[0.9375rem] mb-1">Want a Faster Response?</p>
                    <p className="text-gray-600 text-[0.875rem] leading-[1.75] mb-4">
                      You can also join LINE now and our team will assist you with confirming your needs.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href="https://lin.ee/X7Hcpf4"
                        rel="nofollow"
                        className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer w-full sm:w-auto"
                      >
                        <i className="ri-line-fill text-lg" />
                        Join LINE Now
                      </a>
                      <span className="text-[#4CAF50] text-[0.875rem] font-medium">LINE@ ID: @126uwznp</span>
                    </div>
                  </div>

                  <div className="px-8 py-5">
                    <button
                      onClick={() => setSubmitState('idle')}
                      className="whitespace-nowrap border border-gray-300 text-gray-500 hover:border-[#4CAF50] hover:text-[#4CAF50] font-medium text-[0.875rem] px-6 py-2.5 rounded-full transition-all duration-300 cursor-pointer"
                    >
                      Submit Another Inquiry
                    </button>
                  </div>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="contact_name"
                        type="text"
                        required
                        placeholder="Your full name"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        Company / Brand Name
                      </label>
                      <input
                        name="company_name"
                        type="text"
                        placeholder="Your company or brand name"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="phone"
                        type="tel"
                        required
                        placeholder="Your contact number"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="your@email.com"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      Product Type to Develop
                    </label>
                    <select
                      name="product_type"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer"
                      defaultValue=""
                    >
                      <option value="" disabled>Select product type</option>
                      {productTypes.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      Estimated Production Quantity
                    </label>
                    <select
                      name="quantity"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer"
                      defaultValue=""
                    >
                      <option value="" disabled>Select estimated quantity</option>
                      {quantities.map((q) => (
                        <option key={q} value={q}>{q}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-2">
                      Do You Have a Formula Idea?
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {formulaOptions.map((opt) => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="formula_idea"
                            value={opt}
                            className="accent-[#4CAF50] cursor-pointer"
                          />
                          <span className="text-[#1a2e1a] text-[0.875rem]">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      Requirements{' '}
                      <span className={`text-[0.75rem] font-normal ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
                        ({charCount}/500)
                      </span>
                    </label>
                    <textarea
                      name="requirement"
                      rows={5}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="Please briefly describe your needs, e.g. product direction, target audience, budget planning, collaboration approach, or any additional notes."
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors resize-none bg-white"
                    />
                    {charCount > 500 && (
                      <p className="text-red-500 text-[0.75rem] mt-1">Requirements cannot exceed 500 characters</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitState === 'loading' || charCount > 500}
                    className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] disabled:opacity-60 text-white font-bold text-[1rem] py-4 rounded-lg transition-all duration-300 cursor-pointer"
                  >
                    {submitState === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-lg" />
                        Submit Inquiry
                      </>
                    )}
                  </button>

                  {submitState === 'error' && (
                    <p className="text-red-500 text-[0.875rem] text-center">Submission failed. Please try again or contact us directly.</p>
                  )}

                  <p className="text-gray-400 text-[0.8125rem] text-center leading-[1.75]">
                    After submitting, a specialist will contact you to discuss your product direction, dosage form requirements and collaboration process.
                  </p>

                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-12 bg-[#f7faf7] border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-5">You May Also Be Interested In</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'FAQ', desc: 'Common answers on branding, MOQ, regulations and channels — find answers quickly.', href: '/en/cooperation/faq' },
              { title: 'Cooperation Process', desc: 'From needs alignment to delivery — understand every step of working with Kang Yong.', href: '/en/cooperation/process' },
              { title: 'LINE Consultation', desc: 'Join LINE directly for fast, real-time assistance from our team.', href: 'https://lin.ee/X7Hcpf4' },
            ].map((item) => (
              item.href.startsWith('http') ? (
                <a key={item.href} href={item.href} rel="nofollow" className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer block">
                  <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5">{item.title}</p>
                  <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{item.desc}</p>
                </a>
              ) : (
                <Link key={item.href} to={item.href} className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer block">
                  <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1.5">{item.title}</p>
                  <p className="text-gray-500 text-[0.8125rem] leading-[1.65]">{item.desc}</p>
                </Link>
              )
            ))}
          </div>
        </div>
      </section>
    </EnLayout>
  );
}